<template>
    <div class="card-bg p-4 rounded-lg shadow">
        <div class="flex items-center justify-between mb-4">
            <div class="flex gap-2">
                <Button
                    v-for="option in metricOptions"
                    :key="option.key"
                    :label="option.label"
                    size="small"
                    :severity="selectedMetric === option.key ? 'primary' : 'secondary'"
                    text
                    @click="selectMetric(option.key)"
                />
            </div>
            <div class="flex gap-2">
                <Button
                    v-for="option in rangeOptions"
                    :key="option.days"
                    :label="option.label"
                    size="small"
                    :severity="selectedDays === option.days ? 'primary' : 'secondary'"
                    text
                    @click="selectRange(option.days)"
                />
            </div>
        </div>

        <div class="relative h-[320px]">
            <div v-if="isLoading" class="absolute inset-0 flex items-center justify-center">
                <ProgressSpinner strokeWidth="4" class="w-12 h-12" animationDuration=".7s" />
            </div>
            <Chart
                v-else-if="chartData"
                type="line"
                :data="chartData"
                :options="chartOptions"
                class="h-full"
            />
            <div v-else class="flex items-center justify-center h-full text-gray-500 text-sm">
                No revenue data for this period.
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import Chart from 'primevue/chart';
import ProgressSpinner from 'primevue/progressspinner';
import AdminService from '@/services/AdminService';

const rangeOptions = [
    { label: '3d', days: 3 },
    { label: '7d', days: 7 },
];

const metricOptions = [
    { label: 'Revenue', key: 'revenue' },
    { label: 'Clicks',  key: 'clicks'  },
    { label: 'FTDs',    key: 'ftds'    },
];

const selectedDays = ref(3);
const selectedMetric = ref('revenue');
const isLoading = ref(false);
const chartData = ref(null);

// Distinct colours for up to 10 publishers
const COLOURS = [
    '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
    '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16'
];

function getDateRange(days) {
    const to = new Date();
    to.setDate(to.getDate() - 1); // exclude today — data is delayed
    const from = new Date(to);
    from.setDate(from.getDate() - (days - 1));
    return {
        from: from.toISOString().split('T')[0],
        to: to.toISOString().split('T')[0]
    };
}

function buildChartData(rawData, days, metric = 'revenue') {
    // Generate all dates in range as labels
    const labels = [];
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const base = new Date(yesterday);
    base.setDate(base.getDate() - (days - 1));
    for (let i = 0; i < days; i++) {
        const d = new Date(base);
        d.setDate(d.getDate() + i);
        labels.push(d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' }));
    }

    // Group by publisher
    const publisherMap = {};
    rawData.forEach(entry => {
        const dateStr = new Date(entry.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' });
        if (!publisherMap[entry.publisherName]) {
            publisherMap[entry.publisherName] = {};
        }
        publisherMap[entry.publisherName][dateStr] =
            (publisherMap[entry.publisherName][dateStr] || 0) + entry[metric];
    });

    // Filter out publishers with zero revenue across the entire period
    const publishers = Object.keys(publisherMap).filter(name =>
        Object.values(publisherMap[name]).some(v => v > 0)
    );
    if (!publishers.length) return null;

    const datasets = publishers.map((name, i) => ({
        label: name,
        data: labels.map(date => +(publisherMap[name][date] || 0).toFixed(2)),
        borderColor: COLOURS[i % COLOURS.length],
        backgroundColor: COLOURS[i % COLOURS.length] + '33',
        tension: 0.4,
        fill: false,
        pointRadius: days <= 7 ? 4 : 2,
    }));

    return { labels, datasets };
}

const chartOptions = {
    maintainAspectRatio: false,
    plugins: {
        legend: {
            position: 'top',
            labels: { usePointStyle: true, color: 'white', font: { size: 11 } }
        },
        tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
                label: ctx => {
                    const v = ctx.parsed.y;
                    return selectedMetric.value === 'revenue'
                        ? ` ${ctx.dataset.label}: €${v.toFixed(2)}`
                        : ` ${ctx.dataset.label}: ${v}`;
                }
            }
        }
    },
    scales: {
        x: {
            ticks: { color: 'white', font: { size: 10 }, maxTicksLimit: 15 },
            grid: { color: '#565555' }
        },
        y: {
            beginAtZero: true,
            ticks: { color: 'white', font: { size: 11 }, callback: v => selectedMetric.value === 'revenue' ? `€${v}` : v },
            grid: { color: '#565555' }
        }
    },
    interaction: { intersect: false, mode: 'index' }
};

async function fetchData() {
    isLoading.value = true;
    chartData.value = null;
    try {
        const { from, to } = getDateRange(selectedDays.value);
        const res = await AdminService.getPublisherDailyRevenue(from, to);
        chartData.value = buildChartData(res.data, selectedDays.value, selectedMetric.value);
    } catch (err) {
        console.error('Error fetching publisher daily revenue:', err);
    } finally {
        isLoading.value = false;
    }
}

function selectRange(days) {
    selectedDays.value = days;
    fetchData();
}

function selectMetric(metric) {
    selectedMetric.value = metric;
    fetchData();
}

onMounted(fetchData);
</script>
